<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from "vue";
import { CreditListResponse } from "~/api/auction/types";

const props = withDefaults(
  defineProps<{
    creditList: CreditListResponse["activeCredits"][number];
  }>(),
  {}
);

const creditAmount = props.creditList.remaining || 0;
const expirationDate = props.creditList.expirationDate;

const calculateTimeLeft = (expiration: Date) => {
  const now = new Date();
  const difference = expiration.getTime() - now.getTime();
  const seconds = Math.floor((difference / 1000) % 60);
  const minutes = Math.floor((difference / (1000 * 60)) % 60);
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const days = Math.floor(difference / (1000 * 60 * 60 * 24));

  if (difference < 0) {
    return {
      days: "00",
      hours: "00",
      minutes: "00",
      seconds: "00",
    };
  }

  return {
    days: String(days).padStart(2, "0"),
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
};

const timeLeft = ref({
  days: "00",
  hours: "00",
  minutes: "00",
  seconds: "00",
});

let intervalId: NodeJS.Timeout | null = null;

const updateTimer = () => {
  if (expirationDate) {
    timeLeft.value = calculateTimeLeft(new Date(expirationDate));
  }
};

const startTimer = () => {
  if (intervalId) clearInterval(intervalId);
  updateTimer();
  intervalId = setInterval(updateTimer, 1000);
};

const stopTimer = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

onMounted(() => startTimer());
onUnmounted(() => stopTimer());

watch(
  () => props.creditList,
  (newList) => {
    if (newList.length > 0) {
      // Update expiration date
      const newExpirationDate = newList[0].expirationDate;
      if (newExpirationDate) {
        startTimer();
      }
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex flex-col items-center justify-start gap-2 py-4 pt-3 px-5 bg-slate-900 disabled:opacity-15 text-white rounded-lg border border-slate-200"
  >
    <div class="text-sm font-medium text-slate-200">
      {{ creditAmount }} crédits expireront dans ...
    </div>
    <div class="bg-slate-200 w-full rounded-lg flex justify-center items-center flex-col">
      <div
        class="grid grid-flow-col gap-4 w-full place-items-center mx-4 max-w-56 text-slate-200 font-medium"
      >
        <div
          class="text-slate-950 rounded-lg w-full h-full text-center place-content-center min-w-7 flex items-center justify-center"
        >
          <div class="w-2.5 relative overflow-hidden h-full">
            <Transition name="slide" mode="out-in">
              <span
                :key="timeLeft.days[0]"
                class="absolute inset-0 flex items-center justify-center"
              >
                {{ timeLeft.days[0] }}
              </span>
            </Transition>
          </div>
          <div class="w-2.5 relative overflow-hidden h-full">
            <Transition name="slide" mode="out-in">
              <span
                :key="timeLeft.days[1]"
                class="absolute inset-0 flex items-center justify-center"
              >
                {{ timeLeft.days[1] }}
              </span>
            </Transition>
          </div>
        </div>
        <div class="text-slate-950">:</div>
        <div
          class="text-slate-950 rounded-lg w-full h-full text-center place-content-center min-w-7 flex items-center justify-center"
        >
          <div class="w-2.5 relative overflow-hidden h-full">
            <Transition name="slide" mode="out-in">
              <span
                :key="timeLeft.hours[0]"
                class="absolute inset-0 flex items-center justify-center"
              >
                {{ timeLeft.hours[0] }}
              </span>
            </Transition>
          </div>
          <div class="w-2.5 relative overflow-hidden h-full">
            <Transition name="slide" mode="out-in">
              <span
                :key="timeLeft.hours[1]"
                class="absolute inset-0 flex items-center justify-center"
              >
                {{ timeLeft.hours[1] }}
              </span>
            </Transition>
          </div>
        </div>
        <div class="text-slate-950">:</div>
        <div
          class="text-slate-950 rounded-lg w-full h-full text-center place-content-center min-w-7 flex items-center justify-center"
        >
          <div class="w-2.5 relative overflow-hidden h-full">
            <Transition name="slide" mode="out-in">
              <span
                :key="timeLeft.minutes[0]"
                class="absolute inset-0 flex items-center justify-center"
              >
                {{ timeLeft.minutes[0] }}
              </span>
            </Transition>
          </div>
          <div class="w-2.5 relative overflow-hidden h-full">
            <Transition name="slide" mode="out-in">
              <span
                :key="timeLeft.minutes[1]"
                class="absolute inset-0 flex items-center justify-center"
              >
                {{ timeLeft.minutes[1] }}
              </span>
            </Transition>
          </div>
        </div>
        <div class="text-slate-950">:</div>
        <div
          class="text-slate-950 rounded-lg w-full h-full text-center place-content-center min-w-7 flex items-center justify-center"
        >
          <div class="w-2.5 relative overflow-hidden h-full">
            <Transition name="slide" mode="out-in">
              <span
                :key="timeLeft.seconds[0]"
                class="absolute inset-0 flex items-center justify-center"
              >
                {{ timeLeft.seconds[0] }}
              </span>
            </Transition>
          </div>
          <div class="w-2.5 relative overflow-hidden h-full">
            <Transition name="slide" mode="out-in">
              <span
                :key="timeLeft.seconds[1]"
                class="absolute inset-0 flex items-center justify-center"
              >
                {{ timeLeft.seconds[1] }}
              </span>
            </Transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.2s ease-in-out;
}

.slide-enter-from {
  transform: translateY(-50%);
}

.slide-leave-to {
  transform: translateY(50%);
}
</style>
